<template>
  <v-dialog
    v-model="internalModelValue"
    width="450px"
    :fullscreen="isMobile"
  >
    <v-card>
      <v-card-title class="delete-material-dialog__header">
        <span>{{ $t('ad-materials.delete.title') }}</span>
        <v-btn
          icon="mdi-close"
          variant="text"
          color="white"
          @click="close"
        />
      </v-card-title>
      <v-card-text class="pt-6">
        <p class="text-body-1">
          {{ $t('ad-materials.delete.description') }}
        </p>
        <p
          v-if="material"
          class="text-body-2 font-weight-bold mt-4"
        >
          {{ material.displayName }}
        </p>
      </v-card-text>
      <v-card-actions
        :class="{
          'delete-material-dialog__actions-padding': isMobile,
        }"
      >
        <v-btn
          color="grey-darken-3"
          variant="text"
          @click="close"
          :disabled="isDeleting"
        >
          {{ $t('labels.cancel') }}
        </v-btn>
        <v-spacer />
        <v-btn
          color="error"
          variant="flat"
          @click="deleteMaterial"
          :loading="isDeleting"
        >
          {{ $t('labels.delete') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script lang="ts" setup>
import * as Sentry from '@sentry/vue'
import { useMutation } from '@vue/apollo-composable'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useDisplay } from 'vuetify'

import { showSnackbarMessage } from '@/lib/snackbarMessages'

import DELETE_AI_GENERATED_MATERIAL from './DeleteAiGeneratedMaterial.gql'
import DELETE_USER_UPLOADED_MATERIAL from './DeleteUserUploadedMaterial.gql'
import type { FlattenedMaterial } from './index.vue'

type Props = {
  modelValue: boolean
  material: FlattenedMaterial | null
}

const props = defineProps<Props>()
const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void
  (e: 'material-deleted', materialId: string): void
}>()

const { t } = useI18n()
const { smAndDown: isMobile } = useDisplay()

// Apollo mutations
const { mutate: deleteUserUploadedMaterialMutation, loading: isDeletingUser } = useMutation(
  DELETE_USER_UPLOADED_MATERIAL
)
const { mutate: deleteAiGeneratedMaterialMutation, loading: isDeletingAi } = useMutation(
  DELETE_AI_GENERATED_MATERIAL
)

const isDeleting = computed(() => isDeletingUser.value || isDeletingAi.value)

const internalModelValue = computed({
  get: () => props.modelValue,
  set: (value: boolean) => emit('update:modelValue', value),
})

const close = () => {
  emit('update:modelValue', false)
}

const deleteMaterial = async () => {
  if (!props.material) return

  const mapper = {
    AI: {
      mutation: deleteAiGeneratedMaterialMutation,
      result: (result: any) => result?.data?.deleteAiGeneratedMaterial?.success,
      error: (result: any) => result?.data?.deleteAiGeneratedMaterial?.error,
    },
    USER: {
      mutation: deleteUserUploadedMaterialMutation,
      result: (result: any) => result?.data?.deleteUserUploadedMaterial?.success,
      error: (result: any) => result?.data?.deleteUserUploadedMaterial?.error,
    },
  }

  const materialSource = props.material.imageSource as 'AI' | 'USER'

  if (!mapper[materialSource]) return

  try {
    const result = await mapper[materialSource].mutation({
      input: {
        materialId: props.material.id,
      },
    })

    const success = mapper[materialSource].result(result)
    const error = mapper[materialSource].error(result)

    if (success) {
      showSnackbarMessage('success', t('ad-materials.delete.success'))
      emit('material-deleted', props.material.id)
      close()
    } else {
      throw new Error(error || 'Delete failed')
    }
  } catch (error: any) {
    Sentry.captureException(error, {
      tags: {
        component: 'DeleteMaterialDialog',
        action: 'deleteMaterial',
        materialId: props.material?.id,
      },
    })
    showSnackbarMessage('error', t('ad-materials.delete.error'))
  }
}
</script>

<style scoped>
.delete-material-dialog__header {
  background-color: rgb(var(--v-theme-error));
  color: white;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.delete-material-dialog__actions-padding {
  padding-bottom: 5rem;
}
</style>
